<template>
  <section>
    <b-card header="Order Items">
      <b-row class='d-none d-sm-flex justify-content-center' v-if="!rmaMode">
        <b-col cols=2>
          <center>
            <b-form-checkbox v-model=showAvailability name=show-availability switch>
              {{showAvailability ? 'Hide' : 'Show'}} Availability
            </b-form-checkbox>
          </center>
        </b-col>
        <b-col v-if=partInfoAccess cols=2>
          <center>
            <b-form-checkbox v-model=showCost name=show-cost switch :disabled="!partInfoAccess" v-b-popover.hover="'Viewing Cost on orders is restricted. If you believe you should have access to this functionality, ask IT for access!'">
              <i class="fas fa-fw fa-user-lock"></i>
              {{showCost ? 'Hide' : 'Show'}} Cost
            </b-form-checkbox>
          </center>
        </b-col>
      </b-row>
      <b-card header="Item Discrepancies" header-bg-variant=warning header-text-variant=dark v-if="shownMonoSourceItems.length > 0 && !rmaMode" class='mb-2'>
        These items were either ONLY found in Fishbowl, or ONLY found on the website
        <b-table :items=shownMonoSourceItems :fields=fields>
          <template v-for="key in fields" :slot="(!!key['key'] ? key['key'] : key)" slot-scope=row>
            <data-merger :key="(!!key['key'] ? key['key'] : key)" :dict="row.item[(!!key['key'] ? key['key'] : key)]" title="Honestly don't remember" />
          </template>
          <template slot=status slot-scope=row>
            {{Object.keys(row.item.sku).join('') | titleize}} ONLY
          </template>
          <template slot=unitPrice slot-scope=row>
            <data-merger :dict="row.item.unitPrice" formatter=formatDollars title="Unit Price" />
          </template>
          <template slot=totalPrice slot-scope=row>
            <data-merger :dict="row.item.totalPrice" formatter=formatDollars title="Total Price" />
          </template>
          <template slot=unitCost slot-scope=row>
            <data-merger :dict="row.item.unitCost" formatter=formatDollars title="Unit Cost"/>
          </template>
          <template slot=totalCost slot-scope=row>
            <data-merger :dict="row.item.totalCost" formatter=formatDollars title="Total Cost" />
          </template>
          <template slot=qty slot-scope=row>
            <data-merger :dict=row.item.qty title="Quantity" /> <data-merger :dict=row.item.uom.name title="UOM Name" />
            <span v-if="!!row.item.originalQuantity && row.item.qty.shopify != row.item.originalQuantity.shopify">
              <b-button variant="link" style="color:orange" class='m-0 p-0' v-b-popover.hover="`It's likely that this item's qty was modified in shopify!!`" href="#">
                <i class="fas fa-exclamation-triangle"></i>
              </b-button>
            </span>
          </template>
        </b-table>
      </b-card>
      <b-row>
        <b-col v-if="!inFishbowl && onWeb" cols=2>
          <b-alert show variant=danger>This order only exists on {{order.websiteName}}!</b-alert>
        </b-col>
        <b-col v-if="inFishbowl && !onWeb" cols=2>
          <b-alert show variant=danger>This order only exists in Fishbowl!</b-alert>
        </b-col>
        <b-col>
          <b-row v-if="!rmaMode">
            <b-col><h4>Order Items</h4></b-col>
            <b-col>Total Weight: <data-merger title="Total Weight" :dict="order.totalWeightLbs" formatter="roundDouble" :fuzzy="true" /> lbs</b-col>
          </b-row>
          <b-table :items=shownMergedItems :fields=fields striped responsive align-v=center small v-if="shownMergedItems.length > 0">
            <template v-for="key in fields" :slot="(!!key['key'] ? key['key'] : key)" slot-scope=row>
              <data-merger :key="(!!key['key'] ? key['key'] : key)" :dict="row.item[(!!key['key'] ? key['key'] : key)]" :title="key" />
            </template>
            <template slot=status slot-scope=row>
              <b-img
                v-if="row.item.monoSource != 'shopify'"
                :style="{'display': 'block', 'height': '21px'}"
                :class="getRowClass(row)"
                :src="fishbowlStatus(row.item).icon"
                v-b-popover.hover="fishbowlStatus(row.item).name"
                @click="showKit(row.item)"
              />
              <b-img
                v-else-if="row.item.fulfillmentStatus"
                v-b-popover.hover="!!row.item.fulfillmentStatus.shopify ? 'WARNING: Issued in Shopify, not in FB' : 'Unfulfilled'"
                :src="shopifyStatusIcon(row.item)"
                :style="{ 'display': 'block', 'height': '21px' }"
                :class="getRowClass(row)"
              />
            </template>
            <template slot=sku slot-scope=row>
              <data-merger :dict="row.item.sku" fuzzy title="SKU" :linkTo="{ name: 'Qty Calculations', query: { productNum: getSku(row) }}" />
              <span v-if="isBlankItem(row)"><em>(blank item)</em></span>
              <span v-if="getActive(row)"><b>(Item is inactive in Fishbowl)</b></span>
            </template>
            <template slot=description slot-scope=row>
              <data-merger :dict=row.item.description :showWarning="false" title="Item Description" />
              <em class='ml-5 small'>
                <data-merger :dict=row.item.availabilityText v-if='showAvailability' title="Availability Text" />
              </em>
              <div :class="{ 'ml-5': !!row.item.kitItems, small: true }">
                <data-merger :dict=row.item.note title="Note" />
              </div>
            </template>
            <template slot=qty slot-scope=row>
              <data-merger :dict=row.item.qty title="Quantity" /> <data-merger :dict=row.item.uom.name title="UOM Name" />
              <p v-if="typeof(row.item.picks) === 'Array' && row.item.picks.some(x => x.pickItemStatus.name.fishbowl === 'Short')">
                Short {{row.item.picks.filter(x => x.pickItemStatus.name.fishbowl === 'Short').sum(x => x.pickItemQty.fishbowl)}}
              </p>
              <span v-if="!!row.item.originalQuantity && row.item.qty.shopify != row.item.originalQuantity.shopify">
                <b-button variant="link" style="color:orange" class='m-0 p-0' v-b-popover.hover="`It's likely that this item's qty was modified in shopify!!`" href="#">
                  <i class="fas fa-exclamation-triangle"></i>
                </b-button>
              </span>
            </template>
            <template slot=unitPrice slot-scope=row>
              <data-merger :dict="row.item.unitPrice" fuzzy formatter=formatDollars title="Unit Price" />
            </template>
            <template slot=totalPrice slot-scope=row>
              <data-merger :dict="row.item.totalPrice" fuzzy formatter=formatDollars title="Total Price" />
            </template>
            <template slot=unitCost slot-scope=row>
              <data-merger :dict="row.item.unitCost" fuzzy formatter=formatDollars title="Unit Cost" />
            </template>
            <template slot=totalCost slot-scope=row>
              <data-merger :dict="row.item.totalCost" fuzzy formatter=formatDollars title="Total Cost" />
            </template>
            <template slot=actions slot-scope=row>
              <b-btn size=sm @click="$emit('start-return', row.item)" v-if="isReturnable(row.item)">
                <i class="fas fa-exchange-alt"></i>
              </b-btn>
            </template>
          </b-table>
        </b-col>
      </b-row>
      <b-row v-if="!rmaMode">
        <b-col v-for='(items,ix) in invoice' :key=ix>
          <table class='mt-2 table totals'>
            <tbody>
              <tr v-for='(item,i) in items' :key=i :style="{ backgroundColor: item.bg }">
                <td><strong class='text-right'>{{item.label}}</strong></td>
                <td><div class='text-right'>{{item.amount | formatDollars}}</div></td>
              </tr>
            </tbody>
          </table>
        </b-col>
      </b-row>
    </b-card>
  </section>
</template>

<script>
import { mapGetters } from 'vuex';
import orderMerge from '../../../services/orderMerge';
import DataMerger from '../../utility/DataMerger.vue';
import ScreenSizeMixin from '../../../screenSize.js';

import VoidIcon from '../../../assets/images/Void.png';
import ShippedIcon from '../../../assets/images/Shipped.png';
import ReturningIcon from '../../../assets/images/Returning.png';
import PickingIcon from '../../../assets/images/Picking.png';
import EnteredIcon from '../../../assets/images/Entered.png';
import ClosedShortIcon from '../../../assets/images/ClosedShort.png';
import BuildingIcon from '../../../assets/images/Building.png';
import KitIcon from '../../../assets/images/arrow.png';
import ShortIcon from '../../../assets/images/Short.png';
import HoldIcon from '../../../assets/images/Hold.png';
import PartialIcon from '../../../assets/images/Partial.png';
import PickedIcon from '../../../assets/images/Picked.png';
import WarningIcon from '../../../assets/images/Warning.png';
import UnknownIcon from '../../../assets/images/Unknown.png';

export default {
  mixins: [ ScreenSizeMixin ],
  props: {
    order: { type: Object, required: true },
    rmaMode: { type: Boolean, required: false, default: false },
  },
  components: {
    DataMerger
  },
  data() {
    return {
      showAvailability: false,
      showCost: false,
      fields: [ 'sku', 'description' ],
      monoSourceItems: [],
      shownMonoSourceItems: [],
      mergedItems: [],
      shownMergedItems: [],
    };
  },
  watch: {
    breakpoint() {
      this.getFields()
    },
    showCost() {
      this.getFields()
    },
  },
  computed: {
    ...mapGetters({
      canAccess: 'canAccess',
    }),
    partInfoAccess() {
      return this.$store.getters.canAccess('part-info-access')
    },
    inFishbowl() {
      return this.order.status.fishbowl !== 'doesnt_exist'
    },
    onWeb() {
      return this.order.status[this.order.websiteName] !== 'doesnt_exist'
    },
    totalWeight() {
      return Math.round(orderMerge.mergeField(this.order.totalWeightLbs) * 100) / 100
    },
    invoice() {
      let arr = [[
        { label: 'Subtotal', amount: orderMerge.mergeField(this.order.itemTotal)  },
        { label: 'Shipping', amount: orderMerge.mergeField(this.order.shippingTotal) },
        { label: 'Tax', amount: orderMerge.mergeField(this.order.taxTotal) },
        { label: 'Total', amount: orderMerge.mergeField(this.order.totalPrice), bg: 'gainsboro' },
      ]];
      if (this.order.totalDuties) {
        arr[0].splice(2, 0, { label: 'Duties', amount: orderMerge.mergeField(this.order.totalDuties) })
      }

      if (this.showCost && this.partInfoAccess) {
        arr.push([{ label: 'Cost', amount: orderMerge.mergeField(this.order.totalCost) }])
      }
      arr.push(this.order.payments.map((payment) => {
        return { label: `Payment - ${orderMerge.mergeField(payment.method)}${orderMerge.mergeField(payment.amount) < 0 ? ' (REFUND)' : ''}`, amount: orderMerge.mergeField(payment.amount) }
      }))

      return arr.reverse()
    },
  },
  mounted() {
    this.mergedItems = this.order.items.filter(x => !x.monoSource)
    this.monoSourceItems = this.order.items.filter(x => !!x.monoSource);
    if (this.order.sources.length === 1) {
      this.monoSourceItems.forEach(item => this.mergedItems.push(item))
      this.monoSourceItems.splice(0)
    }
    this.shownMonoSourceItems = this.getItems(this.monoSourceItems)
    this.shownMergedItems = this.getItems(this.mergedItems)
  },
  methods: {
    getItems(arr) {
      let items = []
      arr.forEach(item => {
        items.push(item)
        if (!!item.showingKitItems && Array.isArray(item['kitItems'])) {
          item.kitItems.forEach(kitItem => {
            kitItem.isKitItem = true
            if (!this.rmaMode || kitItem.itemType.id.fishbowl != 80) {
              items.push(kitItem)
            }
          })
        }
      })
      return items
    },
    isBlankItem(row) {
      return orderMerge.mergeField(row.item.blank)
    },
    getFields() {
      let arr = ['status', { key: 'sku', label: 'SKU' }, 'description', 'unitPrice', 'totalPrice', 'qty']
      if (this.showCost) arr.splice(arr.indexOf('totalPrice')+1, 0, ...['unitCost', 'totalCost'])

      if ('xs' === this.breakpoint) {
        this.fields = ['status', { key: 'sku', label: 'SKU' }, 'description', 'qty']
      } else {
        this.fields = arr
      }

      if (this.rmaMode) {
        this.fields = [...this.fields, 'actions']
      }
    },
    getRowClass(row) {
      if (row.item.isKitItem) {
        return `ml-4 mb-0`
      } else {
        return `ml-0 mb-0`
      }
    },
    getSku(row) {
      return orderMerge.mergeField(row.item.sku);
    },
    getActive(row) {
      return !!row.item.active && !orderMerge.mergeField(row.item.active)
    },
    fishbowlStatus(row) {
      if (orderMerge.mergeField(row.kit)) {
        return { name: 'Kit', icon: KitIcon }
      }
      if (!row.soItemStatus || !row.soItemStatus.name) {
        return { name: 'Unknown', icon: UnknownIcon }
      }
      switch (orderMerge.mergeField(row.itemType.name)) {
        case 'Credit Return':
          switch (orderMerge.mergeField(row.soItemStatus.name)) {
            case 'Partial':
            case 'Picking':
            case 'Entered':
              return { name: 'Returning', icon: ReturningIcon }
            case 'Fulfilled': return { name: 'Shipped', icon: ShippedIcon }
            default: return { name: 'Unknown', icon: UnknownIcon }
          }
        case 'Sale':
          switch (orderMerge.mergeField(row.soItemStatus.name)) {
            case 'Entered':
              if (row.picks && row.picks.length > 0) {
                // sort picks, grab the minimum status, since we only really care about 5/6
                const picks = [...row.picks].sort((a,b) => a.pickItemStatus.id.fishbowl - b.pickItemStatus.id.fishbowl)
                switch (picks[0].pickItemStatus.name.fishbowl) {
                  case 'Short': return { name: 'Short', icon: ShortIcon }
                  case 'Hold': return { name: 'Hold', icon: HoldIcon }
                  default: return { name: 'Entered', icon: EnteredIcon }
                }
              }
              return { name: 'Entered', icon: EnteredIcon }
            case 'Building': return { name: 'Building', icon: BuildingIcon }
            case 'Picking': return { name: 'Picking', icon: PickingIcon }
            case 'Partial': return { name: 'Partial', icon: PartialIcon }
            case 'Picked': return { name: 'Picked', icon: PickedIcon }
            case 'Fulfilled': return { name: 'Shipped', icon: ShippedIcon }
            case 'ClosedShort': return { name: 'ClosedShort', icon: ClosedShortIcon }
            case 'Voided': return { name: 'Voided', icon: VoidIcon }
            default: return { name: 'Unknown', icon: UnknownIcon }
          }
        default:
          return { name: 'Unknown', icon: UnknownIcon }
      }
    },
    shopifyStatusIcon(row) {
      switch (row.fulfillmentStatus.shopify) {
        case null:
          return EnteredIcon;
        case 'Entered':
          return EnteredIcon;
        case 'Fulfilled':
          return ShippedIcon;
        default: // if we're fulfilled on Shopify but not in FB, something's gone wrong...
          return WarningIcon;
      }
    },
    showKit(item) {
      if (!item.kitItems) return;
      item.showingKitItems = !item.showingKitItems
      this.shownMonoSourceItems = this.getItems(this.monoSourceItems)
      this.shownMergedItems = this.getItems(this.mergedItems)
    },
    isReturnable(item) {
      return !orderMerge.mergeField(item.isKitItem) || orderMerge.mergeField(item.itemType.id) == 10
    }
  }
}
</script>



// WEBPACK FOOTER //
// src/components/sales-order/view-order-2/OrderItems.vue